import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { INotification, Notification } from '../models/notification.model';
import { IServerResponse, ServerResponse } from '../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  public loading: boolean = false;

  /** La lectura falló. Se distingue de "no hay avisos": una lista vacía por un
      500 no puede pintarse como bandeja limpia. */
  public loadFailed: boolean = false;

  public notifications: Notification[] = [];

  /* -------------------------------------------------------------------------- */
  /*  El constructor NO pide nada, y así debe quedarse.                          */
  /*                                                                            */
  /*  Angular instancia los services `providedIn: 'root'` al primer inyectado y  */
  /*  la consola arranca el dashboard inyectando varios a la vez. Una petición   */
  /*  aquí entraría en ese fan-out de arranque, que es justo lo que hoy tumba a  */
  /*  console-api. La carga la dispara el componente de la campana cuando ya     */
  /*  tiene fraccionamiento, y se repite al cambiar de fraccionamiento.          */
  /* -------------------------------------------------------------------------- */
  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public toastService: ToastService
  ) { }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR unseenCount                             */
  /* -------------------------------------------------------------------------- */
  // region unseenCount
  /** Avisos sin ver. Se cuenta sobre la lista ya cargada en vez de pedirle al
      backend un endpoint de conteo: es el mismo dato y una sola petición. */
  get unseenCount(): number {
    return this.notifications.filter(
      notification => !notification.seen
    ).length;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR reset                                */
  /* -------------------------------------------------------------------------- */
  // region reset
  /** Se llama al cambiar de fraccionamiento: los avisos de una colonia no
      pueden seguir contando en el badge de otra ni por un instante. */
  reset() {
    this.notifications = [];
    this.loadFailed = false;
    this.loading = false;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll(
    neighborhoodId: string
  ): Promise<Notification[]> {

    let notifications: Notification[] = [];

    this.loading = true;
    this.loadFailed = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/notifications`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ notifications: INotification[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ notifications: INotification[] }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {

        this.notifications = response.data!.notifications.map(
          notification => new Notification(notification)
        );

        notifications = this.notifications;

      } else {
        /* Sin toast: la campana no puede lanzar un aviso de error encima de la
           pantalla solo por entrar al fraccionamiento. El panel lo cuenta
           cuando el administrador lo abre, con su botón de reintentar. */
        this.loadFailed = true;
      }

    } catch (error) {
      console.log(error);
      this.loadFailed = true;
    }

    this.loading = false;

    return notifications;

  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR markAsSeen                             */
  /* -------------------------------------------------------------------------- */
  // region markAsSeen
  /**
   * Marca un aviso como visto. Es optimista a propósito: el contador baja en el
   * mismo clic, porque el administrador va a navegar al reporte y no puede
   * quedarse mirando un badge que no cambia. Si el PUT falla se revierte y se
   * avisa, que es el único caso en el que vale la pena interrumpir.
   *
   * No usa el spinner global: nada de overlay bloqueante por marcar un aviso.
   */
  async markAsSeen(
    neighborhoodId: string,
    notification: Notification
  ): Promise<boolean> {

    if (notification.seen) {
      return true;
    }

    notification.seen = true;

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/notifications/${notification.notificationId}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{ notification: INotification }>>(
        (resolve, reject) => {
          this.http.put<IServerResponse<{ notification: INotification }>>(
            urlApi,
            JSON.stringify({ seen: true }),
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            }
          });
        }
      );

      flag = response.statusCode === 200;

    } catch (error) {
      console.log(error);
    }

    if (!flag) {
      notification.seen = false;

      this.toastService.show(
        {
          header: 'Error de servidor',
          body: 'El aviso no pudo marcarse como visto.',
          classname: 'bg-danger text-white'
        }
      );
    }

    return flag;

  }

}
